import { useCallback, useEffect, useRef, useState } from 'react';

interface UseUserActivityOptions {
    inactivityThresholdSeconds: number;
    onInactive: () => void;
    enabled: boolean;
}

/**
 * Custom hook to track user activity and trigger callback after inactivity
 * @param inactivityThresholdSeconds - Number of seconds of inactivity before triggering callback
 * @param onInactive - Callback function to call when user is inactive
 * @param enabled - Whether activity tracking is enabled
 * @returns Object with lastActivityTime and resetActivity function
 */
export const useUserActivity = ({
    inactivityThresholdSeconds,
    onInactive,
    enabled
}: UseUserActivityOptions) => {
    const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now());
    const timeoutRef = useRef<number | null>(null);
    const throttleTimerRef = useRef<number | null>(null);
    const hasTriggeredRef = useRef<boolean>(false);
    
    // Keep a ref to the latest onInactive callback to avoid stale closures
    // This ensures token refresh updates are always reflected without re-registering listeners
    const onInactiveRef = useRef(onInactive);
    
    // Update the ref whenever onInactive changes
    useEffect(() => {
        onInactiveRef.current = onInactive;
    }, [onInactive]);

    const resetActivity = useCallback(() => {
        const now = Date.now();
        setLastActivityTime(now);
        hasTriggeredRef.current = false;

        // Clear existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Set new timeout
        if (enabled) {
            timeoutRef.current = setTimeout(() => {
                if (!hasTriggeredRef.current) {
                    hasTriggeredRef.current = true;
                    // Use ref to always call the latest callback
                    onInactiveRef.current();
                }
            }, inactivityThresholdSeconds * 1000) as unknown as number;
        }
    }, [enabled, inactivityThresholdSeconds]);

    useEffect(() => {
        if (!enabled) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            return;
        }

        // Events to track for user activity
        // Note: 'keypress' is deprecated and redundant with 'keydown'
        const events = [
            'mousedown',
            'mousemove',
            'scroll',
            'touchstart',
            'click',
            'keydown'
        ];

        // Throttle to avoid excessive updates (only update once per second)
        const throttledResetActivity = () => {
            if (!throttleTimerRef.current) {
                throttleTimerRef.current = setTimeout(() => {
                    resetActivity();
                    throttleTimerRef.current = null;
                }, 1000) as unknown as number;
            }
        };

        // Add event listeners
        events.forEach(event => {
            window.addEventListener(event, throttledResetActivity);
        });

        // Initialize timer on mount
        resetActivity();

        // Cleanup
        return () => {
            events.forEach(event => {
                window.removeEventListener(event, throttledResetActivity);
            });
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (throttleTimerRef.current) {
                clearTimeout(throttleTimerRef.current);
            }
        };
    }, [enabled, inactivityThresholdSeconds, resetActivity]);

    return {
        lastActivityTime,
        resetActivity
    };
};
